/**
 * Paged MobX store for the redesigned Manage Returns list: pagination, list rows,
 * `status_group` / `include_tab_counts`, cached `tab_counts` for tab labels, and the
 * date column sort state (descending by default).
 * See ai-docs/manage-returns-redesign.md Section 6.
 */
// eslint-disable-next-line
import { computed, makeObservable, observable, runInAction } from "mobx";
import { SORT_TYPES } from "../../../../components/table/table.types";
import dashboardTranslationsStore from "../../../../dashboard-translation/stores/dashboard-translations-store";
import PagedDataStore, { IFetchListArgs } from "../../../../stores/abstract-paged-data-store";
import RmaListItemStore from "../../rma-list-page/stores/rma-item-store";
import RmaListFiltersStore from "../../rma-list-page/stores/rma-list-filters-store";
import RmaListService, { IFetchRmaListArgs, IRmaTabCounts } from "../../../../services/api/rma-list-service";
import { TYPE, rootContainer } from "../../../../services/container/root";
import rmaPageMainStore from "../../rma-page/stores/rma-page-main-store";
import { IIframeService } from "../../../../services/iframe-service/iframe-service";

/** Sortable date column on the list endpoint; the shown date depends on the UI date-field setting. */
export const DATE_SORT_FIELD = "display_date";

export interface IDateColumnSortOption {
    id: SORT_TYPES;
    label: string;
}

const TABLE_SETTINGS_SCOPE = "dashboard-returns-table-settings";

class RmaTabsPagedListStore extends PagedDataStore<RmaListItemStore> {
    @observable private _rmaListFiltersStore: RmaListFiltersStore = new RmaListFiltersStore({
        defaultDateRangeMonths: 3,
    });
    @observable private _isLoaded: boolean = false;
    /** Cached Open / Closed / All counts for tab labels; updated when the API returns `tab_counts`. */
    @observable private _tabCounts: IRmaTabCounts | null = null;

    constructor() {
        super();
        makeObservable(this);
        this._sortBy = DATE_SORT_FIELD;
        /** default sort direction is descending */
        this._sortDir = SORT_TYPES.DESC;
    }

    readonly perPage: number = 20;

    protected get _rmaListService(): RmaListService {
        return rootContainer.get<RmaListService>(TYPE.RmaListService);
    }

    private get _iframeService(): IIframeService {
        return rootContainer.get<IIframeService>(TYPE.IframService);
    }

    get rmaListFiltersStore() {
        return this._rmaListFiltersStore;
    }

    @computed
    get tabCounts(): IRmaTabCounts | null {
        return this._tabCounts;
    }

    /** Asc / Desc choices for the date column settings popover. */
    public get sortOptions(): IDateColumnSortOption[] {
        return [
            {
                id: SORT_TYPES.ASC,
                label: dashboardTranslationsStore.getTranslation("SORT_ASCENDING", TABLE_SETTINGS_SCOPE),
            },
            {
                id: SORT_TYPES.DESC,
                label: dashboardTranslationsStore.getTranslation("SORT_DESCENDING", TABLE_SETTINGS_SCOPE),
            },
        ];
    }

    protected async fetchList(args: IFetchListArgs) {
        try {
            if (!this._isLoaded) {
                await this._rmaListFiltersStore.ensureDefaultDateRangeTimeZone();
            }
            if (this._isLoaded && !this._rmaListFiltersStore.hasChanges() && !rmaPageMainStore.getUpdatedRmas()) {
                return;
            }
            const filters = this._rmaListFiltersStore.getUpdatedModel();
            const rmaListArgs: IFetchRmaListArgs = {
                ...args,
                ...filters,
                include_tab_counts: true,
            } as IFetchRmaListArgs;
            const result = await this._rmaListService.fetchRmaList(rmaListArgs);
            this._rmaListFiltersStore.commitChanges();
            runInAction(() => {
                this._isLoaded = true;
                // Redesigned page always sends `include_tab_counts: true`, but the list still
                // routes to the legacy Laravel proxy (`/rms-api/manage-returns/rma-list`) when
                // `BETA_RMS_API_MANAGE_RETURNS` is off. That endpoint ignores the flag and omits
                // `tab_counts` from the payload. Only rms-api returns counts when opted in.
                // Update the cache only when present so we do not clear labels on Laravel responses.
                if (result?.tab_counts) {
                    this._tabCounts = result.tab_counts;
                }
            });

            return {
                ...result,
                data: result.data.map(rma => new RmaListItemStore(rma)),
            };
        } catch (err) {
            console.error(err);
        }
    }

    public async load() {
        /** handle top frame history changes */
        this._iframeService.navigateReturns({});

        /** if we are navigating from RMA page to the list and the list was not loaded - load and exit */
        if (!this._isLoaded) {
            await this.loadPage(this._page);
            return;
        }

        /** if list was rendered and then some rmas were updated before user navigated back to the list - reload it */
        const updatedRmaIds = rmaPageMainStore.getUpdatedRmas();
        if (updatedRmaIds.length > 0) {
            await this.loadPage(this._page);
            rmaPageMainStore.clearUpdatedRmaIds();
        }
    }

    public resetIsLoaded() {
        this._isLoaded = false;
    }
}

export default new RmaTabsPagedListStore();
